import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import {
  Brain, FileText, Heart, Stethoscope, Receipt,
  BarChart3, Sparkles, ChevronRight
} from 'lucide-react';

const features = [
  {
    id: 'session-guidance',
    icon: Brain,
    title: 'Real-Time Session Guidance',
    subtitle: 'AI that thinks alongside you during every session',
    description: 'Get clinically relevant prompts, risk screening alerts, and evidence-based suggestions in real-time as you conduct therapy sessions. Our AI analyzes patient history, current diagnosis, session information and assessment data to surface what matters most.',
    highlights: [
      'Live AI prompts based on patient history',
      'Risk factor screening during conversation',
      'Evidence-based question suggestions',
      'Session goal tracking and redirection cues',
    ],
    color: 'from-brand-500 to-brand-600',
    preview: {
      label: 'Live Session',
      content: 'Patient expressing increased stress at work. Consider exploring: workplace relationships, sleep patterns, coping mechanisms. PHQ-9 trending: +3 from baseline.',
    },
  },
  {
    id: 'emotion-detection',
    icon: Heart,
    title: 'Emotion Detection & Analysis',
    subtitle: 'See what words alone cannot express',
    description: 'Advanced AI analyzes voice tone, language patterns, and behavioral cues to detect emotional states in real-time. Identify early crisis indicators, track emotional progression across sessions, and gain deeper clinical insights.',
    highlights: [
      'Voice tone and speech pattern analysis',
      'Real-time emotional state detection',
      'Crisis indicator identification',
      'Longitudinal emotion tracking across sessions',
    ],
    color: 'from-rose-500 to-pink-600',
    preview: {
      label: 'Emotion Analysis',
      content: 'Primary: Anxiety (moderate, 72%). Secondary: Sadness (mild, 34%). Pattern: Elevated anxiety when discussing workplace topics. Engagement: Moderate-High.',
    },
  },
  {
    id: 'documentation',
    icon: FileText,
    title: 'Automated SOAP & Progress Notes',
    subtitle: 'From session to documentation in seconds',
    description: 'AI automatically generates structured SOAP, DAP, and progress notes from recorded or live sessions. Notes are clinically accurate, insurance-compliant, and formatted for immediate use without manual editing.',
    highlights: [
      'SOAP, DAP, and custom note formats',
      'Generated from live or recorded sessions',
      'Insurance-compliant documentation',
      'Editable before finalization',
    ],
    color: 'from-accent-500 to-emerald-600',
    preview: {
      label: 'Auto-Generated SOAP Note',
      content: 'S: Patient reports increased anxiety related to work stress. O: Affect anxious, speech rate elevated. A: GAD symptoms moderately increased. P: Continue CBT, introduce workplace mindfulness techniques.',
    },
  },
  {
    id: 'treatment-plans',
    icon: Stethoscope,
    title: 'AI-Powered Treatment Plans',
    subtitle: 'Personalized care plans that evolve with your patients',
    description: 'Generate evidence-based, personalized treatment plans derived from clinical assessments, progress notes, and session insights. Plans automatically adapt based on patient progress and new clinical data.',
    highlights: [
      'Evidence-based plan generation',
      'Auto-updates from session insights',
      'Condition-specific protocols',
      'Goal tracking and milestone alerts',
    ],
    color: 'from-violet-500 to-purple-600',
    preview: {
      label: 'Treatment Plan Update',
      content: 'Goal 1: Reduce anxiety (GAD-7: 14 → target 7). Intervention: CBT + workplace coping strategies. Progress: 30% improvement in 4 sessions. Recommendation: Add mindfulness module.',
    },
  },
  {
    id: 'billing',
    icon: Receipt,
    title: 'ICD Coding & Billing Automation',
    subtitle: 'Stop leaving revenue on the table',
    description: 'AI extracts ICD-10 codes directly from session content and auto-generates CMS-1500 claim forms. Reduce billing errors, minimize claim denials, and accelerate your revenue cycle.',
    highlights: [
      'Automated ICD-10 code extraction',
      'CMS-1500 claim form generation',
      'Denial risk flagging before submission',
      'Revenue cycle optimization',
    ],
    color: 'from-amber-500 to-orange-600',
    preview: {
      label: 'Billing Intelligence',
      content: 'ICD-10: F41.1 (Generalized anxiety disorder). CPT: 90837 (60-min psychotherapy). CMS-1500 ready. Denial risk: Low (3%). Estimated reimbursement: $165.',
    },
  },
  {
    id: 'analytics',
    icon: BarChart3,
    title: 'Longitudinal Outcomes',
    subtitle: 'Custom build outcome algorithm',
    description: 'AI analyzes patterns from patient data, session history, and assessments to provide actionable insights. Track outcomes across PHQ-9, GAD-7, and other measures with automatic scoring and trend analysis.',
    highlights: [
      'PHQ-9, GAD-7, PTSD screener tracking',
      'Automatic scoring and trend analysis',
      'Cross-session pattern recognition',
      'Outcome reporting for stakeholders',
    ],
    color: 'from-cyan-500 to-blue-600',
    preview: {
      label: 'Patient Insight',
      content: 'PHQ-9 trend: 18 → 12 → 9 (improving). GAD-7: Stable at 14. Pattern: Anxiety spikes correlate with Monday sessions. Recommendation: Explore weekend triggers.',
    },
  },
];

export default function AIFeatures() {
  const [activeFeature, setActiveFeature] = useState(0);
  const { ref, inView } = useScrollAnimation();

  return (
    <section id="features" className="section-padding bg-gradient-to-b from-gray-50 to-white" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-sm font-medium text-brand-700 mb-4">
            <Sparkles className="w-4 h-4" />
            AI-First Features
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Six AI Capabilities{' '}
            <span className="gradient-text">No One Else Has</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Every feature is powered by AI specifically trained for behavioral health.
            Not generic AI bolted on. Purpose-built clinical intelligence.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Feature Nav - Left */}
          <div className="lg:col-span-2 space-y-2 min-w-0">
            {features.map((feature, index) => (
              <motion.button
                key={feature.id}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                onClick={() => setActiveFeature(index)}
                className={`w-full text-left p-4 rounded-xl transition-all duration-300 group ${
                  activeFeature === index
                    ? 'bg-white shadow-lg shadow-brand-900/5 border border-brand-200'
                    : 'hover:bg-white/50 border border-transparent'
                }`}
                aria-pressed={activeFeature === index}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-gradient-to-br ${feature.color} ${
                    activeFeature === index ? 'shadow-lg' : 'opacity-60 group-hover:opacity-100'
                  } transition-all`}>
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm font-bold truncate ${
                      activeFeature === index ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-900'
                    } transition-colors`}>
                      {feature.title}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{feature.subtitle}</div>
                  </div>
                  <ChevronRight className={`w-4 h-4 shrink-0 ml-auto transition-all ${
                    activeFeature === index ? 'text-brand-500 rotate-90' : 'text-gray-300'
                  }`} />
                </div>
              </motion.button>
            ))}
          </div>

          {/* Feature Detail - Right */}
          <div className="lg:col-span-3 min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-gray-100 overflow-hidden"
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${features[activeFeature].color} p-6`}>
                  <div className="flex items-center gap-3 mb-2">
                    {React.createElement(features[activeFeature].icon, { className: 'w-6 h-6 text-white' })}
                    <h3 className="text-xl font-bold text-white">{features[activeFeature].title}</h3>
                  </div>
                  <p className="text-white/80 text-sm">{features[activeFeature].subtitle}</p>
                </div>

                <div className="p-6">
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {features[activeFeature].description}
                  </p>

                  {/* Highlights */}
                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {features[activeFeature].highlights.map((highlight, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${features[activeFeature].color} flex items-center justify-center shrink-0`}>
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>

                  {/* Preview */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {features[activeFeature].preview.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 font-mono leading-relaxed">
                      {features[activeFeature].preview.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
